import { useEffect, useState } from "react";
import { Button, Card, CardBody } from "@heroui/react";
import { IconDeviceMobile } from "@tabler/icons-react";
import { useTranslations } from "../../../../../i18n";
import {
  fetchPaymentSettings,
  getTwintPhoneNumber,
  getTwintQrCode,
} from "@/lib/paymentApi";
import Loader from "@/components/loader";
import TwintQrCode from "@/components/twint-qr-code";

interface TwintQrPaymentProps {
  onComplete: () => void;
}

export default function TwintQrPayment({ onComplete }: TwintQrPaymentProps) {
  const t = useTranslations("checkout");
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const settings = await fetchPaymentSettings();
        if (cancelled) return;
        setQrCode(getTwintQrCode(settings));
        setPhoneNumber(getTwintPhoneNumber(settings));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full md:w-[75%] mx-auto flex flex-col justify-center items-center">
      <Card className="w-full p-4 md:p-6" shadow="sm">
        <CardBody className="flex flex-col items-center gap-4 text-center">
          <IconDeviceMobile size={28} className="text-gray-600" />
          <h2 className="text-lg md:text-xl font-semibold">
            {t("twintQrTitle") || "Pay with TWINT"}
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            {t("twintQrPageDescription") ||
              "Your booking has been received. Scan the QR code below with your TWINT app to complete your payment."}
          </p>

          {qrCode && (
            <TwintQrCode
              qrCodeKey={qrCode}
              imageClassName="w-48 h-48 md:w-56 md:h-56"
            />
          )}

          {!qrCode && phoneNumber && (
            <div className="p-3 md:p-4 bg-gray-50 rounded-md w-full">
              <p className="text-sm md:text-base">
                <span className="font-medium">
                  {t("phoneNumber") || "Phone number"}:
                </span>{" "}
                {phoneNumber}
              </p>
            </div>
          )}

          <p className="text-xs md:text-sm text-gray-500">
            {t("twintInstructions") ||
              "Use your TWINT app to send payment. Include your booking reference in the payment message."}
          </p>

          <Button
            color="primary"
            size="lg"
            className="w-full"
            onPress={onComplete}
          >
            {t("twintPaymentDone") || "I've paid, continue"}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
